import React from 'react';
import {
    ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, LabelList
} from 'recharts';
import { GoalContributionData } from '../services/salesRepAPI';

interface GoalContributionChartProps {
    data: GoalContributionData[];
}

const formatCurrency = (value: number) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact' });

const GoalContributionChart: React.FC<GoalContributionChartProps> = ({ data }) => {
    const chartData = [...data].sort((a, b) => b.percentualMeta - a.percentualMeta);

    return (
        <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 16, right: 16, left: 8, bottom: 8 }} barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="nomeVendedor" tick={({ x, y, payload }: any) => {
                    const name = String(payload?.value ?? '').split(' ').slice(0, 1).join(' ');
                    return <text x={x} y={y + 12} textAnchor="middle" fontSize={10} fontWeight={700} fill="var(--text-primary)">{name.length > 10 ? name.substring(0, 9) + '...' : name}</text>;
                }} axisLine={false} tickLine={false} height={40} interval={0} />
                <YAxis tickFormatter={formatCurrency} tick={{ fontSize: 10, fontWeight: 700, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false} />
                <Tooltip content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload;
                    return (
                        <div style={{ background: 'var(--bg-widget)', borderRadius: 'var(--radius-base, 8px)', padding: '8px 12px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', border: '1px solid var(--border-primary)', fontSize: 12, minWidth: 160 }}>
                            <p style={{ fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 6px', fontSize: 11, borderBottom: '1px solid var(--border-primary)', paddingBottom: 4 }}>{label}</p>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '2px 0' }}>
                                <span style={{ color: 'var(--text-secondary)', fontWeight: 600, fontSize: 11 }}>Vendas</span>
                                <span style={{ fontWeight: 800, color: 'var(--text-primary)', fontSize: 12 }}>{formatCurrency(d?.totalVenda)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '2px 0' }}>
                                <span style={{ color: 'var(--text-secondary)', fontWeight: 600, fontSize: 11 }}>Meta</span>
                                <span style={{ fontWeight: 700, color: 'var(--theme-chart-start, #dc2626)', fontSize: 12 }}>{formatCurrency(d?.meta)}</span>
                            </div>
                            {d?.percentualMeta != null && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, borderTop: '1px solid var(--border-primary)', marginTop: 4, paddingTop: 4 }}>
                                    <span style={{ color: 'var(--text-secondary)', fontWeight: 600, fontSize: 11 }}>Resultado</span>
                                    <span style={{ fontWeight: 800, fontSize: 12, color: Number(d.percentualMeta) >= 100 ? '#16a34a' : '#dc2626' }}>{Number(d.percentualMeta).toFixed(2)}%</span>
                                </div>
                            )}
                        </div>
                    );
                }} />
                <Bar dataKey="totalVenda" name="Vendas" fill="var(--theme-accent, #1e293b)" radius={[0, 0, 0, 0]} barSize={36}>
                    <LabelList dataKey="totalVenda" position="top" formatter={(v: any) => { const n = Number(v); return isNaN(n) ? '' : n.toLocaleString('pt-BR', { notation: 'compact' }); }} style={{ fontSize: 10, fontWeight: 700, fill: '#1e293b' }} />
                </Bar>
                <Line type="monotone" dataKey="meta" name="Meta" stroke="var(--theme-chart-start, #dc2626)" strokeWidth={2.5} strokeDasharray="6 3" dot={{ r: 3, fill: 'var(--theme-chart-start, #dc2626)', strokeWidth: 2, stroke: '#fff' }} />
            </ComposedChart>
        </ResponsiveContainer>
    );
};

export default GoalContributionChart;